// Keep this module zod-free: it's client-bundled on every /reports page via
// the @dupe/common/research-report barrel, and a zod import here ships ~90 KB
// gz of schema machinery (incl. all locales) to the browser.
import { ShoppingCategory } from '../types/shoppingCategory'

export const SEO_REPORT_CATEGORY_TAXONOMY_VERSION = '2026-04-28'

export const SEO_REPORT_CATEGORY_SLUGS = [
  'fashion',
  'furniture',
  'home-decor',
  'electronics',
  'beauty',
  'toys',
  'sporting',
  'other',
] as const

export const SEO_REPORT_SUBCATEGORY_SLUGS = [
  'apparel',
  'shoes',
  'bags',
  'jewelry',
  'eyewear',
  'seating',
  'tables',
  'beds',
  'desks',
  'storage',
  'bedding-bath',
  'rugs',
  'lighting',
  'kitchen-dining',
  'decor-accents',
  'audio',
  'computers-accessories',
  'phones-accessories',
  'gaming',
  'cameras-optics',
  'smart-home',
  'skincare',
  'makeup',
  'haircare',
  'fragrance',
  'grooming',
  'games-puzzles',
  'collectibles',
  'dolls-plush',
  'building-toys',
  'outdoor-play',
  'fitness',
  'outdoor-recreation',
  'cycling',
  'team-sports',
  'golf',
  'baby-gear',
  'pet-supplies',
  'office-supplies',
  'food-beverage',
  'automotive',
  'other',
] as const

export type SeoReportCategorySlug = (typeof SEO_REPORT_CATEGORY_SLUGS)[number]
export type SeoReportSubcategorySlug =
  (typeof SEO_REPORT_SUBCATEGORY_SLUGS)[number]

export interface SeoReportSubcategoryDefinition {
  readonly slug: SeoReportSubcategorySlug
  readonly label: string
  readonly keywords: readonly string[]
  readonly isDefault?: boolean
}

export interface SeoReportCategoryDefinition {
  readonly slug: SeoReportCategorySlug
  readonly label: string
  readonly shoppingCategory: Exclude<ShoppingCategory, ShoppingCategory.All>
  readonly leaves: readonly SeoReportSubcategoryDefinition[]
}

export interface SeoReportCategoryPair {
  categorySlug: SeoReportCategorySlug
  subcategorySlug: SeoReportSubcategorySlug
  taxonomyVersion: typeof SEO_REPORT_CATEGORY_TAXONOMY_VERSION
}

export type SeoReportCategorySuggestionConfidence = 'high' | 'medium' | 'low'

export interface SeoReportCategorySuggestion extends SeoReportCategoryPair {
  confidence: SeoReportCategorySuggestionConfidence
  redFlag: boolean
  reason: string
  matchedKeywords: string[]
}

export const SEO_REPORT_CATEGORY_TAXONOMY = [
  {
    slug: 'fashion',
    label: 'Fashion',
    shoppingCategory: ShoppingCategory.Fashion,
    leaves: [
      {
        slug: 'apparel',
        label: 'Apparel',
        isDefault: true,
        keywords: [
          'apparel',
          'clothing',
          'shirt',
          'shirts',
          'dress',
          'dresses',
          'pants',
          'jeans',
          'jacket',
          'coat',
          'activewear',
          'underwear',
        ],
      },
      {
        slug: 'shoes',
        label: 'Shoes',
        keywords: [
          'shoe',
          'shoes',
          'sneaker',
          'sneakers',
          'boot',
          'boots',
          'sandal',
          'sandals',
          'heel',
          'heels',
          'loafer',
          'footwear',
          'running shoes',
        ],
      },
      {
        slug: 'bags',
        label: 'Bags',
        keywords: [
          'bag',
          'bags',
          'handbag',
          'handbags',
          'purse',
          'pouch',
          'backpack',
          'tote',
          'luggage',
          'wallet',
        ],
      },
      {
        slug: 'jewelry',
        label: 'Jewelry',
        keywords: [
          'jewelry',
          'jewellery',
          'ring',
          'rings',
          'necklace',
          'bracelet',
          'earring',
          'earrings',
          'watch',
          'watches',
        ],
      },
      {
        slug: 'eyewear',
        label: 'Eyewear',
        keywords: ['eyewear', 'sunglasses', 'glasses', 'eyeglasses', 'frames'],
      },
    ],
  },
  {
    slug: 'furniture',
    label: 'Furniture',
    shoppingCategory: ShoppingCategory.Furniture,
    leaves: [
      {
        slug: 'seating',
        label: 'Seating',
        isDefault: true,
        keywords: [
          'sofa',
          'sofas',
          'couch',
          'chair',
          'chairs',
          'stool',
          'stools',
          'bench',
          'seating',
        ],
      },
      {
        slug: 'tables',
        label: 'Tables',
        keywords: [
          'table',
          'tables',
          'coffee table',
          'dining table',
          'nightstand',
          'side table',
        ],
      },
      {
        slug: 'beds',
        label: 'Beds',
        keywords: ['bed', 'beds', 'mattress', 'crib', 'headboard', 'bed frame'],
      },
      {
        slug: 'desks',
        label: 'Desks',
        keywords: ['desk', 'desks', 'standing desk', 'office desk'],
      },
      {
        slug: 'storage',
        label: 'Storage',
        keywords: [
          'dresser',
          'dressers',
          'bookshelf',
          'shelf',
          'shelves',
          'cabinet',
          'storage',
          'wardrobe',
          'console',
        ],
      },
    ],
  },
  {
    slug: 'home-decor',
    label: 'Home Decor',
    shoppingCategory: ShoppingCategory.HomeDecor,
    leaves: [
      {
        slug: 'decor-accents',
        label: 'Decor Accents',
        isDefault: true,
        keywords: [
          'decor',
          'vase',
          'mirror',
          'art',
          'print',
          'candle',
          'pillow',
          'throw',
          'accent',
        ],
      },
      {
        slug: 'bedding-bath',
        label: 'Bedding & Bath',
        keywords: [
          'bedding',
          'sheet',
          'sheets',
          'duvet',
          'comforter',
          'towel',
          'bath',
          'bathroom',
        ],
      },
      {
        slug: 'rugs',
        label: 'Rugs',
        keywords: ['rug', 'rugs', 'carpet', 'runner'],
      },
      {
        slug: 'lighting',
        label: 'Lighting',
        keywords: [
          'lamp',
          'lamps',
          'lighting',
          'light',
          'chandelier',
          'sconce',
        ],
      },
      {
        slug: 'kitchen-dining',
        label: 'Kitchen & Dining',
        keywords: [
          'kitchen',
          'dining',
          'dinnerware',
          'cookware',
          'glassware',
          'plate',
          'plates',
          'pan',
          'knife',
        ],
      },
    ],
  },
  {
    slug: 'electronics',
    label: 'Electronics',
    shoppingCategory: ShoppingCategory.Electronics,
    leaves: [
      {
        slug: 'audio',
        label: 'Audio',
        isDefault: true,
        keywords: [
          'headphone',
          'headphones',
          'earbuds',
          'speaker',
          'speakers',
          'soundbar',
          'audio',
        ],
      },
      {
        slug: 'computers-accessories',
        label: 'Computers & Accessories',
        keywords: [
          'laptop',
          'computer',
          'keyboard',
          'mouse',
          'monitor',
          'ssd',
          'gpu',
          'usb',
          'charger',
          'cable',
        ],
      },
      {
        slug: 'phones-accessories',
        label: 'Phones & Accessories',
        keywords: [
          'phone',
          'iphone',
          'android',
          'case',
          'screen protector',
          'mobile',
        ],
      },
      {
        slug: 'gaming',
        label: 'Gaming',
        keywords: [
          'gaming',
          'console',
          'controller',
          'playstation',
          'xbox',
          'nintendo',
        ],
      },
      {
        slug: 'cameras-optics',
        label: 'Cameras & Optics',
        keywords: [
          'camera',
          'cameras',
          'lens',
          'lenses',
          'binocular',
          'binoculars',
          'optics',
        ],
      },
      {
        slug: 'smart-home',
        label: 'Smart Home',
        keywords: [
          'smart home',
          'smart speaker',
          'security camera',
          'thermostat',
          'air purifier',
          'filter',
        ],
      },
    ],
  },
  {
    slug: 'beauty',
    label: 'Beauty',
    shoppingCategory: ShoppingCategory.Beauty,
    leaves: [
      {
        slug: 'skincare',
        label: 'Skincare',
        isDefault: true,
        keywords: [
          'skincare',
          'skin care',
          'serum',
          'moisturizer',
          'cleanser',
          'sunscreen',
          'retinol',
        ],
      },
      {
        slug: 'makeup',
        label: 'Makeup',
        keywords: [
          'makeup',
          'foundation',
          'concealer',
          'blush',
          'lipstick',
          'mascara',
          'powder',
        ],
      },
      {
        slug: 'haircare',
        label: 'Haircare',
        keywords: [
          'hair',
          'haircare',
          'shampoo',
          'conditioner',
          'dryer',
          'straightener',
          'curling iron',
        ],
      },
      {
        slug: 'fragrance',
        label: 'Fragrance',
        // Scent-name policy: multi-word scent names and tokens that are
        // unambiguous on their own only. Bare fragrance-house names (creed,
        // chanel, tom ford, …) are deliberately excluded — those brands also
        // sell fashion, so "creed hoodie dupe" / "chanel flap bag dupe" would
        // score here too, and the matcher has no co-occurrence guard.
        keywords: [
          'fragrance',
          'perfume',
          'cologne',
          'scent',
          'parfum',
          'eau de toilette',
          'edp',
          'edt',
          'body mist',
          'attar',
          'baccarat rouge',
          'br540',
          'santal 33',
          'creed aventus',
          'aventus',
          'lost cherry',
          'tobacco vanille',
          'oud wood',
          'delina',
          'mojave ghost',
          'ysl libre',
          'louis vuitton imagination',
          'black opium',
          'bleu de chanel',
          'coco mademoiselle',
          'la vie est belle',
          'dior sauvage',
          'flowerbomb',
          'club de nuit',
          'bianco latte',
        ],
      },
      {
        slug: 'grooming',
        label: 'Grooming',
        keywords: ['grooming', 'razor', 'shaver', 'trimmer', 'beard', 'nail'],
      },
    ],
  },
  {
    slug: 'toys',
    label: 'Toys',
    shoppingCategory: ShoppingCategory.Toys,
    leaves: [
      {
        slug: 'games-puzzles',
        label: 'Games & Puzzles',
        isDefault: true,
        keywords: [
          'game',
          'games',
          'board game',
          'puzzle',
          'puzzles',
          'card game',
        ],
      },
      {
        slug: 'collectibles',
        label: 'Collectibles',
        keywords: [
          'collectible',
          'collectibles',
          'action figure',
          'figure',
          'trading card',
        ],
      },
      {
        slug: 'dolls-plush',
        label: 'Dolls & Plush',
        keywords: ['doll', 'dolls', 'plush', 'stuffed animal'],
      },
      {
        slug: 'building-toys',
        label: 'Building Toys',
        keywords: ['lego', 'blocks', 'building toy', 'building toys'],
      },
      {
        slug: 'outdoor-play',
        label: 'Outdoor Play',
        keywords: ['outdoor play', 'swing', 'trampoline', 'sandbox'],
      },
    ],
  },
  {
    slug: 'sporting',
    label: 'Sporting',
    shoppingCategory: ShoppingCategory.Sporting,
    leaves: [
      {
        slug: 'fitness',
        label: 'Fitness',
        isDefault: true,
        keywords: [
          'fitness',
          'treadmill',
          'dumbbell',
          'weights',
          'yoga',
          'exercise',
          'workout',
        ],
      },
      {
        slug: 'outdoor-recreation',
        label: 'Outdoor Recreation',
        keywords: [
          'outdoor',
          'camping',
          'hiking',
          'backpack',
          'tent',
          'fishing',
        ],
      },
      {
        slug: 'cycling',
        label: 'Cycling',
        keywords: ['bike', 'bicycle', 'cycling', 'e-bike', 'ebike'],
      },
      {
        slug: 'team-sports',
        label: 'Team Sports',
        keywords: [
          'basketball',
          'soccer',
          'football',
          'baseball',
          'volleyball',
          'sport',
        ],
      },
      {
        slug: 'golf',
        label: 'Golf',
        keywords: ['golf', 'club', 'clubs', 'putter'],
      },
    ],
  },
  {
    slug: 'other',
    label: 'Other',
    shoppingCategory: ShoppingCategory.Other,
    leaves: [
      {
        slug: 'other',
        label: 'Other',
        isDefault: true,
        keywords: ['other', 'general', 'miscellaneous'],
      },
      {
        slug: 'baby-gear',
        label: 'Baby Gear',
        keywords: [
          'baby',
          'stroller',
          'car seat',
          'bottle',
          'diaper',
          'toddler',
        ],
      },
      {
        slug: 'pet-supplies',
        label: 'Pet Supplies',
        keywords: ['pet', 'dog', 'cat', 'bird', 'aquarium', 'leash'],
      },
      {
        slug: 'office-supplies',
        label: 'Office Supplies',
        keywords: ['office', 'paper', 'pen', 'notebook', 'printer'],
      },
      {
        slug: 'food-beverage',
        label: 'Food & Beverage',
        keywords: ['food', 'coffee', 'tea', 'snack', 'beverage', 'drink'],
      },
      {
        slug: 'automotive',
        label: 'Automotive',
        keywords: ['car', 'auto', 'automotive', 'vehicle', 'truck'],
      },
    ],
  },
] as const satisfies readonly SeoReportCategoryDefinition[]

const CATEGORY_BY_SLUG = new Map(
  SEO_REPORT_CATEGORY_TAXONOMY.map((category) => [category.slug, category])
)

// Explicitly type the key as the full ShoppingCategory enum so callers can
// safely look up the new buckets (Pets, BabyKids, Auto, Food, Hardware,
// Office, Media — DUP-614). The Map only has entries for the 8 categories
// with SEO report definitions; new buckets return undefined, which is the
// correct fallback in the call sites that consume this lookup.
const CATEGORY_BY_SHOPPING_CATEGORY: Map<
  ShoppingCategory,
  SeoReportCategoryDefinition
> = new Map(
  SEO_REPORT_CATEGORY_TAXONOMY.map((category) => [
    category.shoppingCategory,
    category,
  ])
)

function normalizeComparable(value: string): string {
  return value.trim().toLowerCase().replace(/&/g, 'and').replace(/\s+/g, ' ')
}

function normalizeSlugLike(value: string): string {
  return normalizeComparable(value)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

function textContainsKeyword(text: string, keyword: string): boolean {
  const normalizedText = normalizeComparable(text)
  const normalizedKeyword = normalizeComparable(keyword)
  if (!normalizedKeyword) return false

  return new RegExp(
    `(^|[^a-z0-9])${escapeRegExp(normalizedKeyword)}([^a-z0-9]|$)`,
    'i'
  ).test(normalizedText)
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export function getSeoReportCategory(
  slug: string | null | undefined
): SeoReportCategoryDefinition | undefined {
  if (!slug) return undefined
  return CATEGORY_BY_SLUG.get(slug as SeoReportCategorySlug)
}

export function getSeoReportSubcategory(
  categorySlug: string | null | undefined,
  subcategorySlug: string | null | undefined
): SeoReportSubcategoryDefinition | undefined {
  const category = getSeoReportCategory(categorySlug)
  if (!category || !subcategorySlug) return undefined
  return category.leaves.find((leaf) => leaf.slug === subcategorySlug)
}

export function getSeoReportLeafOptions(
  categorySlug: string | null | undefined
): readonly SeoReportSubcategoryDefinition[] {
  return getSeoReportCategory(categorySlug)?.leaves ?? []
}

export function isValidSeoReportCategorySlug(
  slug: string | null | undefined
): slug is SeoReportCategorySlug {
  return Boolean(getSeoReportCategory(slug))
}

export function isValidSeoReportSubcategorySlug(
  slug: string | null | undefined
): slug is SeoReportSubcategorySlug {
  return Boolean(
    slug && (SEO_REPORT_SUBCATEGORY_SLUGS as readonly string[]).includes(slug)
  )
}

export function isValidSeoReportCategoryPair(input: {
  categorySlug: string | null | undefined
  subcategorySlug: string | null | undefined
}): input is {
  categorySlug: SeoReportCategorySlug
  subcategorySlug: SeoReportSubcategorySlug
} {
  return Boolean(
    getSeoReportSubcategory(input.categorySlug, input.subcategorySlug)
  )
}

export function normalizeSeoReportCategorySlug(
  value: string | null | undefined
): SeoReportCategorySlug | undefined {
  if (!value) return undefined
  const candidate = normalizeSlugLike(value)
  const category = SEO_REPORT_CATEGORY_TAXONOMY.find((entry) => {
    return (
      entry.slug === candidate ||
      normalizeSlugLike(entry.label) === candidate ||
      normalizeSlugLike(entry.shoppingCategory) === candidate
    )
  })
  return category?.slug
}

export function normalizeSeoReportSubcategorySlug(
  categorySlug: string | null | undefined,
  value: string | null | undefined
): SeoReportSubcategorySlug | undefined {
  const category = getSeoReportCategory(categorySlug)
  if (!category || !value) return undefined
  const candidate = normalizeSlugLike(value)
  const leaf = category.leaves.find((entry) => {
    return (
      entry.slug === candidate || normalizeSlugLike(entry.label) === candidate
    )
  })
  return leaf?.slug
}

export function shoppingCategoryToSeoReportCategorySlug(
  category: ShoppingCategory | null | undefined
): SeoReportCategorySlug | undefined {
  if (!category || category === ShoppingCategory.All) return undefined
  return CATEGORY_BY_SHOPPING_CATEGORY.get(category)?.slug
}

export function seoReportCategorySlugToShoppingCategory(
  categorySlug: string | null | undefined
): Exclude<ShoppingCategory, ShoppingCategory.All> | undefined {
  return getSeoReportCategory(categorySlug)?.shoppingCategory
}

export function getDefaultSeoReportSubcategorySlug(
  categorySlug: string | null | undefined
): SeoReportSubcategorySlug | undefined {
  const category = getSeoReportCategory(categorySlug)
  return (
    category?.leaves.find((leaf) => leaf.isDefault)?.slug ??
    category?.leaves[0]?.slug
  )
}

export function parseSeoReportCategoryPair(input: {
  categorySlug?: string | null
  subcategorySlug?: string | null
}): SeoReportCategoryPair | null {
  const categorySlug = normalizeSeoReportCategorySlug(input.categorySlug)
  if (!categorySlug) return null
  const subcategorySlug = normalizeSeoReportSubcategorySlug(
    categorySlug,
    input.subcategorySlug
  )
  if (!subcategorySlug) return null

  return {
    categorySlug,
    subcategorySlug,
    taxonomyVersion: SEO_REPORT_CATEGORY_TAXONOMY_VERSION,
  }
}

export function inferSeoReportSubcategorySlug(input: {
  categorySlug: string | null | undefined
  text: string
}): SeoReportSubcategorySlug | undefined {
  const category = getSeoReportCategory(input.categorySlug)
  if (!category) return undefined

  for (const leaf of category.leaves) {
    if (
      leaf.keywords.some((keyword) => textContainsKeyword(input.text, keyword))
    ) {
      return leaf.slug
    }
  }

  return getDefaultSeoReportSubcategorySlug(category.slug)
}

export function suggestSeoReportCategoryPair(input: {
  text: string | null | undefined
}): SeoReportCategorySuggestion {
  const text = input.text ?? ''
  let best: {
    category: SeoReportCategoryDefinition
    leaf: SeoReportSubcategoryDefinition
    score: number
    matchedKeywords: string[]
  } | null = null

  for (const category of SEO_REPORT_CATEGORY_TAXONOMY) {
    for (const leaf of category.leaves) {
      const matchedKeywords = leaf.keywords.filter((keyword) =>
        textContainsKeyword(text, keyword)
      )
      let score = matchedKeywords.reduce((total, keyword) => {
        const phraseWeight = normalizeComparable(keyword).includes(' ') ? 4 : 3
        return total + phraseWeight
      }, 0)

      if (textContainsKeyword(text, category.label)) score += 1
      if (textContainsKeyword(text, leaf.label)) score += 1

      if (!best || score > best.score) {
        best = { category, leaf, score, matchedKeywords }
      }
    }
  }

  if (!best || best.score <= 0) {
    return {
      categorySlug: 'other',
      subcategorySlug: 'other',
      taxonomyVersion: SEO_REPORT_CATEGORY_TAXONOMY_VERSION,
      confidence: 'low',
      redFlag: true,
      reason:
        'No controlled taxonomy keyword matched this SEO keyword; review or expand the taxonomy before launch.',
      matchedKeywords: [],
    }
  }

  const confidence: SeoReportCategorySuggestionConfidence =
    best.score >= 3 ? 'high' : 'medium'

  return {
    categorySlug: best.category.slug,
    subcategorySlug: best.leaf.slug,
    taxonomyVersion: SEO_REPORT_CATEGORY_TAXONOMY_VERSION,
    confidence,
    redFlag: false,
    reason: `Matched ${best.leaf.label} in ${best.category.label}.`,
    matchedKeywords: best.matchedKeywords,
  }
}

/**
 * Infer a `(categorySlug, subcategorySlug)` pair from free-form text by
 * scanning leaf keywords across the full taxonomy. Used by the
 * deterministic-map fallback in `classifyResearchReportCategory` when
 * the agent failed and there's no caller-provided category. Returns the
 * first leaf whose keywords match — order is taxonomy declaration order,
 * so put the more specific categories earlier in the array if you need
 * a tiebreak.
 */
export function inferSeoReportCategoryFromText(input: {
  text: string
}): SeoReportCategoryPair | undefined {
  for (const category of SEO_REPORT_CATEGORY_TAXONOMY) {
    for (const leaf of category.leaves) {
      if (
        leaf.keywords.some((keyword) =>
          textContainsKeyword(input.text, keyword)
        )
      ) {
        return {
          categorySlug: category.slug,
          subcategorySlug: leaf.slug,
          taxonomyVersion: SEO_REPORT_CATEGORY_TAXONOMY_VERSION,
        }
      }
    }
  }
  return undefined
}

export function buildSeoReportCategoryPromptOptions(
  categorySlug?: string | null
): string {
  const categories = categorySlug
    ? SEO_REPORT_CATEGORY_TAXONOMY.filter(
        (category) => category.slug === categorySlug
      )
    : SEO_REPORT_CATEGORY_TAXONOMY

  return categories
    .map((category) => {
      const leaves = category.leaves
        .map((leaf) => `${leaf.slug} (${leaf.label})`)
        .join(', ')
      return `- ${category.slug} (${category.label}): ${leaves}`
    })
    .join('\n')
}

export function buildSeoReportCategoryToolDescription(): string {
  return SEO_REPORT_CATEGORY_TAXONOMY.map((category) => {
    const leaves = category.leaves.map((leaf) => leaf.slug).join(', ')
    return `${category.slug}: ${leaves}`
  }).join(' | ')
}
